import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExclamationCircleOutlined } from '@ant-design/icons';

import { Tag, Tooltip } from 'antd';
import RiskAssessmentService from '../../../services/RiskAssessment.service';
import { useTranslation } from 'react-i18next';
import ModalFail from '../../../components/Shared/modal/fail';
import { ROUTER } from '../../../constants/router';
import usePermission from '../../../hooks/usePermission';
import classNames from 'classnames';

export default function Header() {
    const { t, i18n } = useTranslation();
    const [cardData, setCardData] = useState();
    const [riskId, setRiskId] = useState();
    const [openFail, setOpenFail] = useState(false);
    const navigate = useNavigate();

    const { isAuditor } = usePermission();

    const getRiskAssessmentHeader = async () => {
        try {
            const { data } =
                await RiskAssessmentService.getRiskAssessmentHeader();
            if (data?.resultData) {
                const item = data?.resultData[0];
                setCardData(item);
                setRiskId(item?.riskAssessmentId);
            }
        } catch (err) {
            console.log(err);
        }
    };

    const createRiskAssessment = async () => {
        try {
            const { data } =
                await RiskAssessmentService.createRiskAssessmentHeader();
            if (data?.resultData) {
                const id = data?.resultData?.riskAssessmentId;
                navigate(`${ROUTER.RISK_MANAGEMENT.RA}/${id}`);
            } else {
                setOpenFail(true);
            }
        } catch (err) {
            console.log(err);
        }
    };

    useEffect(() => {
        getRiskAssessmentHeader();
    }, []);

    const _getTagStatus = (e) => {
        switch (e) {
            case 'Draft':
                return { color: '#65707A', borderColor: '#65707A' };
            case 'Waiting for Review':
                return { color: '#F9C153', borderColor: '#F9C153' };
            case 'Reviewed':
                return { color: '#4ACE8F', borderColor: '#4ACE8F' };
            case 'Revise':
                return { color: '#F0536F', borderColor: '#F0536F' };
            case 'Publish':
                return { color: '#FFFFFF', backgroundColor: '#7B67FB' };
            default:
                return { color: '#FFF', background: '' };
        }
    };

    return (
        <div className="bg-white rounded-xl pt-6 my-4 col-span-2 transform duration-300 hover:scale-[1.01] border-hover">
            <div
                className="pb-2 px-4"
                style={{ borderBottom: '3px solid #F4F6FC' }}
            >
                <div className="flex space-x-2 items-center">
                    <div className="text-xl font-[700]">
                        {t('RA.OrganizationRiskReport')}
                    </div>
                    <Tooltip
                        placement="right"
                        overlayClassName="min-w-[320px]"
                        title={<div>{t('RA.HeaderTootip')}</div>}
                    >
                        <button className="bg-purple-light p-2 flex items-center justify-center rounded-lg text-purple">
                            <ExclamationCircleOutlined />
                        </button>
                    </Tooltip>
                </div>
            </div>
            {!cardData ? (
                <div className="text-center pt-8 flex flex-col items-center justify-center w-full">
                    <button
                        disabled={isAuditor}
                        onClick={() => {
                            createRiskAssessment();
                        }}
                        className={classNames(
                            'relative w-max cursor-pointer group',
                            {
                                'opacity-50 cursor-not-allowed': isAuditor,
                            }
                        )}
                    >
                        <svg
                            width="63"
                            height="63"
                            viewBox="0 0 63 63"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <circle
                                opacity="0.1"
                                cx="31.5"
                                cy="31.5"
                                r="31.5"
                                fill="#A597FF"
                            />
                            <circle
                                opacity="0.2"
                                cx="31.5"
                                cy="31.5"
                                r="23.625"
                                fill="#A597FF"
                            />
                            <circle
                                cx="31.2951"
                                cy="31.4999"
                                r="17.1818"
                                fill="#A597FF"
                            />
                            <g clipPath="url(#clip0_5827_89193)">
                                <path
                                    d="M32.3356 25.0482H29.9067V29.906H25.0488V32.3349H29.9067V37.1928H32.3356V32.3349H37.1934V29.906H32.3356V25.0482Z"
                                    fill="white"
                                />
                            </g>
                            <defs>
                                <clipPath id="clip0_5827_89193">
                                    <rect
                                        width="19.6364"
                                        height="19.6364"
                                        fill="white"
                                        transform="translate(21.4775 21.6819)"
                                    />
                                </clipPath>
                            </defs>
                        </svg>
                        <div className=" 'w-[63px] h-[63px] bg-[#A597FF] opacity-10 rounded-full absolute top-0 group-hover:animate-ping'"></div>
                    </button>
                    <p className="text-[#65707A] text-sm">
                        {t('RA.CreateNew')}
                    </p>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() =>
                        navigate(`${ROUTER.RISK_MANAGEMENT.RA}/${riskId}/view`)
                    }
                    className="px-4 w-full p-4 cursor-pointer rounded-b-xl text-left"
                >
                    <div className="grid grid-cols-2 mt-2">
                        <div className="space-y-2">
                            <div className="grid grid-cols-2 text-xs">
                                <strong className="text-xs">
                                    {t('RList.ReviewBy')} :
                                </strong>
                                <div>{cardData?.reviewerFullName || '-'}</div>
                            </div>
                            <div className="grid grid-cols-2 text-xs">
                                <strong className="text-xs">
                                    {t('RList.Version')} :
                                </strong>
                                <div>{cardData?.version || '-'}</div>
                            </div>
                            <div className="grid grid-cols-2 text-xs">
                                <strong className="text-xs">
                                    {t('RList.Status')} :{' '}
                                </strong>
                                <div>
                                    {cardData?.statusEn ? (
                                        <Tag
                                            className="rounded-xl -my-1 text-center"
                                            style={_getTagStatus(
                                                cardData?.statusEn
                                            )}
                                        >
                                            {i18n?.language === 'en'
                                                ? cardData?.statusEn || '-'
                                                : cardData?.statusTh || '-'}
                                        </Tag>
                                    ) : (
                                        <>-</>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <div className="grid grid-cols-2 text-xs">
                                <strong className="text-xs">
                                    {t('RList.PublishDate')} :
                                </strong>
                                <div>{cardData?.publicDate || '-'}</div>
                            </div>
                            <div className="grid grid-cols-2 text-xs">
                                <strong className="text-xs">
                                    {t('RList.LastUpdate')} :
                                </strong>
                                <div>{cardData?.modifyDate || '-'}</div>
                            </div>
                            <div className="grid grid-cols-2 text-xs">
                                <strong className="text-xs">
                                    {t('RList.UpdateBy')} :{' '}
                                </strong>
                                <div>{cardData?.modifyByFullName || '-'}</div>
                            </div>
                        </div>
                    </div>
                </button>
            )}
            <ModalFail
                open={openFail}
                handelClose={() => {
                    setOpenFail(false);
                }}
                handelOk={() => {
                    setOpenFail(false);
                }}
                text={<p className="lowercase">{t('RA.CreateFail')}</p>}
            />
        </div>
    );
}
